import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { WORKFLOW_STEPS, workflowStep } from "./skillFichaHelpers";

export default function SkillFichaWorkflowBar({
  status,
  designCompletedAt,
  compact = false,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const current = workflowStep(status, designCompletedAt);

  return (
    <Box
      sx={{
        p: compact ? 1.25 : { xs: 1.5, sm: 2 },
        borderRadius: compact ? 2 : 3,
        bgcolor: alpha(theme.palette.background.paper, compact ? 0.7 : 0.9),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {!compact && (
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ mb: 1.5, display: "block" }}
        >
          {t("skillFicha.progress")}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={{ xs: 1, md: compact ? 1.5 : 0 }}
        justifyContent="space-between"
        flexWrap={compact ? "nowrap" : "wrap"}
        sx={{ overflowX: compact ? "auto" : "visible" }}
      >
        {WORKFLOW_STEPS.map((step, index) => {
          const isTerminal =
            status === "published" || status === "skill_created";
          const done =
            current > step.key || (isTerminal && current >= step.key);
          const active = !done && current === step.key;
          return (
            <Stack
              key={step.key}
              direction="row"
              spacing={0.85}
              alignItems="center"
              sx={{
                flex: compact ? "0 0 auto" : 1,
                opacity: done || active ? 1 : 0.5,
                pr: {
                  md: !compact && index < WORKFLOW_STEPS.length - 1 ? 1 : 0,
                },
              }}
            >
              {done ? (
                <CheckCircleOutlineIcon
                  color="success"
                  sx={{ fontSize: compact ? 18 : 20 }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{
                    fontSize: compact ? 18 : 20,
                    color: active ? "primary.main" : "text.disabled",
                  }}
                />
              )}
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={active ? 800 : 600}
                  color={active ? "primary.main" : "text.primary"}
                  sx={{
                    fontSize: compact ? "0.78rem" : undefined,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
                {!compact && (
                  <Typography variant="caption" color="text.secondary">
                    {t(step.hintKey)}
                  </Typography>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
